import { CSSProperties, FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Notes, PhotoLibrary } from '@mui/icons-material'
import { alpha, Box, Button, ButtonGroup, IconButton, MenuItem, Select, Stack, Tooltip, Typography, useTheme } from '@mui/material'

import { GRTable, GRTableColumn, SortOrder } from '../../../../../components/GRTable/GRTable'
import { GameIcon } from '../../../../../features/game'
import { BulkAssignFeature, BulkAssignGame, GameChoiceMap } from '../../types/BulkAssign'
import { formatGps, getChoiceLabel } from '../helpers/bulkAssignHelpers'

// More choices than this are picked from a select, like legacy.
const maxChoiceButtons = 2

type CustomProps = {
  feature: BulkAssignFeature
  answers: GameChoiceMap
  changedIds: Set<string>
  marketIso?: string
  benchmarks: BulkAssignFeature[]
  benchmarkAnswers: Record<number, GameChoiceMap | undefined>
  disabled: boolean
  onSelectChoice: (gameId: string, weightId: number) => void
  onShowNotes: (game: BulkAssignGame, feature: BulkAssignFeature) => void
  onShowScreenshots: (game: BulkAssignGame, feature: BulkAssignFeature) => void
}

type BulkAssignGamesTableProps = CustomProps & { games: BulkAssignGame[] }

const ChoiceCell: FC<{ game: BulkAssignGame; props: CustomProps }> = ({ game, props }) => {
  const { t } = useTranslation()
  const choices = props.feature.choices ?? []
  const answer = props.answers[game.id]
  if (choices.length <= maxChoiceButtons) {
    return (
      <ButtonGroup size="small" disabled={props.disabled}>
        {choices.map((choice) => (
          <Button
            key={choice.weightId}
            variant={answer === choice.weightId ? 'contained' : 'outlined'}
            aria-pressed={answer === choice.weightId}
            aria-label={t('internal-analysts:bulk_assign_choice_for', { choice: choice.choice, game: game.name })}
            onClick={() => props.onSelectChoice(game.id, choice.weightId)}
          >
            {choice.choice}
          </Button>
        ))}
      </ButtonGroup>
    )
  }
  return (
    <Select
      size="small"
      displayEmpty
      value={answer ?? ''}
      disabled={props.disabled}
      onChange={(event) => props.onSelectChoice(game.id, Number(event.target.value))}
      inputProps={{ 'aria-label': t('internal-analysts:bulk_assign_choice_of', { game: game.name }) }}
      sx={{ minWidth: 160 }}
    >
      <MenuItem value="" disabled>
        {t('internal-analysts:bulk_assign_select_answer')}
      </MenuItem>
      {choices.map((choice) => (
        <MenuItem key={choice.weightId} value={choice.weightId}>
          {choice.choice}
        </MenuItem>
      ))}
    </Select>
  )
}

const BenchmarkCell: FC<{ game: BulkAssignGame; feature: BulkAssignFeature; props: CustomProps }> = ({ game, feature, props }) => {
  const { t } = useTranslation()
  const label = getChoiceLabel(feature.choices, props.benchmarkAnswers[feature.legacyId]?.[game.id])
  return (
    <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
      <Typography variant="body2" sx={{ minWidth: 0 }}>
        {props.benchmarkAnswers[feature.legacyId] ? label ?? '---' : '…'}
      </Typography>
      <Box display="flex" flexShrink={0}>
        <Tooltip title={t('internal-analysts:bulk_assign_notes')}>
          <IconButton
            size="small"
            aria-label={t('internal-analysts:bulk_assign_notes_for', { feature: feature.name, game: game.name })}
            onClick={() => props.onShowNotes(game, feature)}
          >
            <Notes fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('internal-analysts:bulk_assign_screenshots')}>
          <IconButton
            size="small"
            aria-label={t('internal-analysts:bulk_assign_screenshots_for', { feature: feature.name, game: game.name })}
            onClick={() => props.onShowScreenshots(game, feature)}
          >
            <PhotoLibrary fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Stack>
  )
}

const benchmarkColumn = (index: number): GRTableColumn<BulkAssignGame, CustomProps> => ({
  labelAccessor: ({ customTableProps }) => customTableProps?.benchmarks[index]?.name ?? '',
  accessor: ({ row, customTableProps }) => {
    const feature = customTableProps?.benchmarks[index]
    return feature && customTableProps ? <BenchmarkCell game={row} feature={feature} props={customTableProps} /> : null
  },
  hidden: ({ customTableProps }) => !customTableProps?.benchmarks[index],
  headerCellProps: { sx: { minWidth: 220 } },
})

export const BulkAssignGamesTable: FC<BulkAssignGamesTableProps> = ({ games, ...customProps }) => {
  const { t } = useTranslation()
  const theme = useTheme()

  const tableColumns: GRTableColumn<BulkAssignGame, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-analysts:bulk_assign_icon'),
        accessor: ({ row }) => <GameIcon src={row.icons?.length ? row.icons : row.icon ?? undefined} gameName={row.name} size="small" />,
        headerCellProps: { sx: { width: 70, minWidth: 70 } },
      },
      {
        labelAccessor: t('internal-analysts:bulk_assign_game'),
        accessor: ({ row }) => (
          <Box textAlign="left">
            <Typography variant="body2" fontWeight="bold">
              {row.name}
            </Typography>
            {row.artist && (
              <Typography variant="caption" color="text.secondary">
                {row.artist}
              </Typography>
            )}
          </Box>
        ),
        sortable: true,
        sortAccessor: ({ row }) => (row.name ?? '').toLowerCase(),
        sortOrder: SortOrder.DESC,
        defaultSortOrder: SortOrder.DESC,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 240 } },
      },
      {
        labelAccessor: t('internal-analysts:bulk_assign_subgenre'),
        accessor: ({ row }) => row.conventionalSubgenre || '---',
        sortable: true,
        sortAccessor: ({ row }) => (row.conventionalSubgenre ?? '').toLowerCase(),
        defaultSortOrder: SortOrder.DESC,
        // Legacy made room for the benchmark columns this way.
        hidden: ({ customTableProps }) => !!customTableProps?.benchmarks.length,
        headerCellProps: { sx: { minWidth: 140 } },
      },
      {
        labelAccessor: t('internal-analysts:bulk_assign_gps'),
        accessor: ({ row, customTableProps }) => formatGps(row, customTableProps?.marketIso),
        sortable: true,
        sortAccessor: ({ row, customTableProps }) => (customTableProps?.marketIso ? row.gpsPerMarket?.[customTableProps.marketIso] ?? 0 : 0),
        headerCellProps: { sx: { width: 80 } },
      },
      {
        labelAccessor: t('internal-analysts:bulk_assign_choice'),
        accessor: ({ row, customTableProps }) => (customTableProps ? <ChoiceCell game={row} props={customTableProps} /> : null),
        sortable: true,
        sortAccessor: ({ row, customTableProps }) => (customTableProps?.changedIds.has(row.id) ? 1 : 0),
        headerCellProps: { sx: { minWidth: 160 } },
      },
      benchmarkColumn(0),
      benchmarkColumn(1),
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)

  const changedStyle = useMemo<CSSProperties>(() => ({ backgroundColor: alpha(theme.palette.warning.main, 0.12) }), [theme])
  const getRowStyle = useCallback(
    (row: BulkAssignGame): CSSProperties => (customProps.changedIds.has(row.id) ? changedStyle : {}),
    [customProps.changedIds, changedStyle]
  )

  return (
    <GRTable
      rows={games}
      columns={columns}
      onColumnsUpdated={setColumns}
      rowIdKey={(row) => row.id}
      customProps={customProps}
      getRowStyle={getRowStyle}
      noRowsLabel={t('internal-analysts:bulk_assign_no_games')}
      hoverable
    />
  )
}
